// UserSubscription.jsx
import React, { useState, useContext } from 'react';
import {
  Grid,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Typography,
  Button,
  Chip,
  Divider,
  Box,
  Link,
  Skeleton,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

import UpgradeMembership from '../Modals/Subscription/upgradeMembership.js';
import FreeTrial from '../Modals/freeTrial.js';
import PaymentDue from '../Modals/Subscription/paymentDue.js';
import ConfirmCancelSubscription from '../Modals/Subscription/confirmCancel.js';

import { UserContext } from '../../context/User';

const brand = '#7A64EB';
const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '—';
const cap = (s = '') => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const DetailsRow = ({ label, value, valueSx, divider = true }) => (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.25 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={valueSx}>
        {value}
      </Typography>
    </Box>
    {divider && <Divider />}
  </>
);

export default function UserSubscription({
  userDetails,
  subscriptions = [],
  currentSubscription = null,
  subscriptionsLoading = false,
  onRefresh, // callback from parent to refetch after actions
}) {
  const { user } = useContext(UserContext);
  const useUser = userDetails || user;

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [trialModalIsOpen, setTrialModalIsOpen] = useState(false);
  const [paymentDueIsOpen, setPaymentDueIsOpen] = useState(false);
  const [cancelSubscriptionIsOpen, setCancelSubscriptionIsOpen] = useState(false);

  const hasInvoiceDue = Boolean(useUser?.invoiceDue && useUser.invoiceDue.length > 0);

  // When the upgrade modal closes, optionally refresh
  function closeMembershipModal(goToFreeTrial) {
    setModalIsOpen(false);
    if (goToFreeTrial && !userDetails) setTrialModalIsOpen(true);
    else onRefresh?.();
  }
  function closeTrialModal() {
    setTrialModalIsOpen(false);
    // You could call onRefresh?.() instead of a hard reload:
    // onRefresh?.();
    window.location.reload();
  }

  // Fallbacks if no subscription found
  const sub = currentSubscription ||
    subscriptions.find((s) => !s?.end_date) ||
    subscriptions[0] || {
      plan: 'free',
      type: 'Free',
      frequency: 'Monthly',
      price: 0,
      next_recurring: null,
      end_date: null,
      status: 'Inactive',
    };

  const rawPlan = String(sub?.plan || '').toLowerCase();
  const rawType = String(sub?.type || '').toLowerCase();
  const isFreeTrial = rawPlan === 'free' || rawType === 'free';
  const isPro = rawPlan === 'pro' || rawType.includes('pro');

  const planName = isFreeTrial ? 'Free Trial' : sub?.type ? `${cap(sub.type)} Plan` : 'Free Plan';
  const price = typeof sub?.couponPrice === 'number' ? sub.couponPrice : sub?.price ?? 0;

  // Features
  const proFeatures = [
    'Generate Shots in Every Scene',
    'One Click full script shot generation',
    'More Detailed Shots: including camera, movement, focus and more',
    'Flexible Export Options in PDF or CSV',
    'Add images to your shots and include them in exports',
  ];
  const freeFeatures = [
    'Upload your project and get started',
    '5 free scene generations for two projects',
    'Manual shot editing and creation',
    'Export your shotlist in PDF',
    'Ability to upgrade your plan and existing projects anytime',
  ];
  const features = isPro ? proFeatures : freeFeatures;

  // Status & cancel logic
  // `end_date` alone just means the subscription won't renew (e.g. a
  // time-limited trial running its course); it's only a user-initiated
  // cancellation when `cancel_date` is also set.
  const isCanceled = Boolean(sub?.cancel_date);
  const isEnding = Boolean(sub?.end_date);
  const statusText = isCanceled
    ? `Canceled — ending on ${fmtDate(sub.end_date)}`
    : isEnding
    ? `Active — ending on ${fmtDate(sub.end_date)}`
    : sub?.status
    ? cap(sub.status)
    : 'Active';
  const statusColorSx = isCanceled || isEnding
    ? { color: 'warning.main', fontWeight: 600 }
    : { color: 'success.main', fontWeight: 600 };

  // Simple skeletons while preloading
  const PriceSkeleton = () => (
    <Box sx={{ textAlign: 'center' }}>
      <Skeleton variant="text" width={120} height={48} />
      <Skeleton variant="text" width={80} />
    </Box>
  );

  const DetailsSkeleton = () => (
    <Stack spacing={1.5}>
      {[...Array(4)].map((_, i) => (
        <Skeleton key={i} variant="rectangular" height={28} />
      ))}
      <Skeleton variant="rectangular" height={40} sx={{ mt: 1 }} />
    </Stack>
  );

  return (
    <>
      {/* Modals */}
      {!isFreeTrial && (
        <ConfirmCancelSubscription
          currentSubscription={sub}
          setIsOpen={setCancelSubscriptionIsOpen}
          isOpen={cancelSubscriptionIsOpen}
          onRefresh={onRefresh}
        />
      )}
      <PaymentDue modalIsOpen={paymentDueIsOpen} setModalIsOpen={setPaymentDueIsOpen} />
      <UpgradeMembership
        isOpen={modalIsOpen}
        onRequestClose={closeMembershipModal}
        includeFree={false}
        userDetails={useUser}
        currentSubscription={sub}
        startingStep={1}
      />
      <FreeTrial isOpen={trialModalIsOpen} onRequestClose={closeTrialModal} />

      <Grid container spacing={2}>
        {/* Left: Current Plan */}
        <Grid item xs={12} md={6}>
          <Card sx={{ minHeight: 360 }}>
            <CardHeader
              title="Current Plan"
              action={
                <Button
                  onClick={() => setModalIsOpen(true)}
                  variant="contained"
                  disabled={subscriptionsLoading}
                >
                  Change Plan
                </Button>
              }
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Stack spacing={2.5} alignItems="center">
                <Chip
                  label={planName.toUpperCase()}
                  sx={{
                    borderRadius: '999px',
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    bgcolor: '#EEE9FF',
                    color: brand,
                    marginLeft: '10px',
                  }}
                />

                {subscriptionsLoading ? (
                  <PriceSkeleton />
                ) : (
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 44, fontWeight: 800, lineHeight: 1, mb: 0.25 }}>
                      {currency.format(price)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      per month
                    </Typography>
                  </Box>
                )}

                {/* Features */}
                <Stack spacing={1.0} sx={{ mt: 0.5, width: '100%' }}>
                  {features.map((f) => (
                    <Stack key={f} direction="row" spacing={1.25} alignItems="center">
                      <CheckRoundedIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2">{f}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Right: Subscription Details */}
        <Grid item xs={12} md={6}>
          <Card sx={{ minHeight: 360 }}>
            <CardHeader title="Subscription Details" sx={{ pb: 0 }} />
            <CardContent>
              {subscriptionsLoading ? (
                <DetailsSkeleton />
              ) : (
                <Stack spacing={0}>
                  {/* Status */}
                  <DetailsRow label="Status" value={statusText} valueSx={statusColorSx} />

                  <DetailsRow label="Renewal Frequency" value={sub?.frequency || '—'} />

                  {/* Next Renewal Date — HIDE when canceled or ending */}
                  {!isEnding && (
                    <DetailsRow
                      label="Next Renewal Date"
                      value={
                        hasInvoiceDue ? (
                          <Link
                            component="button"
                            underline="hover"
                            onClick={() => setPaymentDueIsOpen(true)}
                            sx={{ fontWeight: 600, color: brand }}
                          >
                            Payment Due – Update
                          </Link>
                        ) : (
                          fmtDate(sub?.next_recurring)
                        )
                      }
                    />
                  )}

                  <DetailsRow label="Subscription Ends" value={fmtDate(sub?.end_date)} />

                  {!isFreeTrial && (
                    <DetailsRow
                      label="Auto-Renewal"
                      value={isEnding ? 'Disabled' : 'Enabled'}
                      divider={false}
                    />
                  )}

                  {/* Cancel button — HIDE when free OR already canceled */}
                  {!isFreeTrial && !isCanceled && (
                    <Box sx={{ mt: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        sx={{
                          backgroundColor: '#F2F2F2 !important',
                          color: '#111 !important',
                          py: 1.25,
                          boxShadow: 'none',
                          '&:hover': { backgroundColor: '#E6E6E6 !important', boxShadow: 'none' },
                        }}
                        onClick={() => setCancelSubscriptionIsOpen(true)}
                      >
                        Cancel Subscription
                      </Button>
                    </Box>
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
